import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import LanguageSelector from "@/components/LanguageSelector";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Mail, Lock } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_f50ba61d-1f02-4136-b6b8-0e3c71c5b623/artifacts/e4hujy2b_sologo-aitools-bgremover-trimmed.png";

export default function LoginPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await login(email, password);
      toast.success(t('auth.success.login'));
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F7FA] flex flex-col" data-testid="login-page">
      {/* Background decoration */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#20C997] rounded-full opacity-10 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-[#1FB6FF] rounded-full opacity-10 blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#1E2A5A] rounded-full opacity-[0.02] blur-3xl" />
      </div>

      {/* Header */}
      <header className="relative z-10 p-6 flex items-center justify-between">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-[#6B7280] hover:text-[#1E2A5A] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-medium">{t('common.back')}</span>
        </Link>
        <LanguageSelector variant="minimal" />
      </header>

      {/* Main content */}
      <main className="relative z-10 flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-block">
              <img src={LOGO_URL} alt="Diffuzo" className="h-10 mx-auto" />
            </Link>
          </div>

          {/* Card */}
          <div className="bg-white rounded-3xl p-8 soft-shadow">
            {/* Title */}
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold font-heading text-[#1E2A5A] mb-2">
                {t('auth.login.title')}
              </h1>
              <p className="text-[#6B7280]">
                {t('auth.login.subtitle')}
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#1E2A5A] font-medium">
                  {t('auth.login.email')}
                </Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280]" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="vous@exemple.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="login-email-input"
                    className="h-12 pl-12 rounded-xl border-gray-200 focus:border-[#20C997] focus:ring-[#20C997] transition-colors"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-[#1E2A5A] font-medium">
                    {t('auth.login.password')}
                  </Label>
                  <Link 
                    to="#" 
                    className="text-sm text-[#20C997] hover:text-[#1E2A5A] transition-colors"
                  >
                    {t('auth.login.forgot')}
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280]" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="login-password-input"
                    className="h-12 pl-12 rounded-xl border-gray-200 focus:border-[#20C997] focus:ring-[#20C997] transition-colors"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full h-12 bg-[#1E2A5A] hover:bg-[#20C997] text-white font-medium rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-[#20C997]/25"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    {t('common.loading')}
                  </>
                ) : (
                  t('auth.login.submit')
                )}
              </Button>
            </form>

            {/* Divider */}
            <div className="relative my-8">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-white px-4 text-sm text-[#6B7280]">{t('common.or')}</span>
              </div>
            </div>

            {/* Register link */}
            <div className="text-center">
              <p className="text-[#6B7280]">
                {t('auth.login.noAccount')}{" "}
                <Link 
                  to="/register" 
                  className="text-[#20C997] hover:text-[#1E2A5A] font-medium transition-colors"
                  data-testid="register-link"
                >
                  {t('auth.login.createAccount')}
                </Link>
              </p>
            </div>
          </div>

          {/* Footer text */}
          <p className="text-center mt-8 text-sm text-[#6B7280]">
            {t('auth.login.terms')}{" "}
            <Link to="#" className="text-[#1E2A5A] hover:text-[#20C997] transition-colors">
              {t('auth.login.termsLink')}
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
}
